/**
 * Video Editor — frontend API client (Phase B3).
 *
 * One typed hook over the real `/api/video-editor` backend (B1/B2/B3). All
 * calls are MSAL-authenticated the same way the rest of CellcoHub is
 * (Authorization bearer + x-user-email), matching ReviewDashboard's pattern.
 */
import { useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import type { User, VideoProject, ProjectDetail, VideoJob, EditPlan, MusicTrack, AudioMode } from './types';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const base = `${backendUrl}/api/video-editor`;

export type PipelineCommand = 'run' | 'analyse' | 'plan' | 'render';
export type ShipAction = 'download' | 'mcl' | 'archive';

export function useVideoEditorApi(user: User | null) {
  const { instance } = useMsal();

  return useMemo(() => {
    async function authHeaders(): Promise<Record<string, string>> {
      if (!user) throw new Error('Not authenticated');
      if (user.isExternal) {
        const token = user.token || sessionStorage.getItem('externalUserToken') || '';
        return {
          Authorization: `Bearer ${token}`,
          'x-user-email': user.email,
          'x-user-name': user.name,
          'x-external-user': 'true',
        };
      }
      const r = await instance.acquireTokenSilent({ scopes: ['User.Read'], account: user.account });
      return {
        Authorization: `Bearer ${r.accessToken}`,
        'x-user-email': user.email,
        'x-user-name': user.name,
      };
    }

    return {
      async listProjects(): Promise<VideoProject[]> {
        const r = await axios.get(`${base}/projects`, { headers: await authHeaders() });
        return Array.isArray(r.data) ? r.data : [];
      },

      async getProject(id: string): Promise<ProjectDetail> {
        const r = await axios.get(`${base}/projects/${id}`, { headers: await authHeaders() });
        return r.data;
      },

      async createProject(body: { title: string; description?: string }): Promise<VideoProject> {
        const r = await axios.post(`${base}/projects`, body, { headers: await authHeaders() });
        return r.data;
      },

      async createSample(): Promise<VideoProject> {
        const r = await axios.post(`${base}/sample`, {}, { headers: await authHeaders() });
        return r.data;
      },

      async updateProject(id: string, patch: Partial<VideoProject>): Promise<VideoProject> {
        const r = await axios.patch(`${base}/projects/${id}`, patch, { headers: await authHeaders() });
        return r.data;
      },

      async uploadClips(
        id: string,
        files: File[],
        onProgress?: (pct: number) => void
      ): Promise<{ project: VideoProject }> {
        const form = new FormData();
        files.forEach(f => form.append('clips', f));
        const r = await axios.post(`${base}/projects/${id}/clips`, form, {
          headers: await authHeaders(),
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        return r.data;
      },

      // Dispatch a staged pipeline command. `input` is forwarded to the job
      // (e.g. { segment } for an STUB-3 per-segment render). Returns the
      // queued VideoJob.
      async dispatch(id: string, command: PipelineCommand, input?: any): Promise<VideoJob> {
        const headers = await authHeaders();
        if (command === 'run') {
          const r = await axios.post(`${base}/projects/${id}/jobs`,
            { type: 'run', input }, { headers });
          return r.data;
        }
        const path = command === 'analyse' ? 'analyze' : command;
        const r = await axios.post(`${base}/projects/${id}/${path}`, { input }, { headers });
        return r.data;
      },

      async getJob(jobId: string): Promise<VideoJob> {
        const r = await axios.get(`${base}/jobs/${jobId}`, { headers: await authHeaders() });
        return r.data;
      },

      async patchEditPlan(id: string, editPlan: EditPlan): Promise<VideoProject> {
        const r = await axios.patch(`${base}/projects/${id}/edit_plan`, { editPlan },
          { headers: await authHeaders() });
        return r.data;
      },

      async ship(id: string, action: ShipAction): Promise<any> {
        const r = await axios.post(`${base}/projects/${id}/ship`, { action },
          { headers: await authHeaders() });
        return r.data;
      },

      // --- Background music library ---

      // List the committed CC0 music library.
      async listMusic(): Promise<MusicTrack[]> {
        const r = await axios.get(`${base}/music`, { headers: await authHeaders() });
        return Array.isArray(r.data?.tracks) ? r.data.tracks : [];
      },

      // Set (or clear, with trackId null) the project's background-music track.
      async setProjectMusic(
        id: string, trackId: string | null, audioMode?: AudioMode
      ): Promise<VideoProject> {
        const r = await axios.put(`${base}/projects/${id}/music`,
          { trackId, audioMode }, { headers: await authHeaders() });
        return r.data;
      },

      // Fetch a library track as an object URL for inline <audio> preview.
      // GET /music/:file is auth-gated, so a plain <audio src> cannot stream
      // it — the caller must revoke the URL when done.
      async loadMusicPreviewUrl(file: string): Promise<string> {
        const res = await fetch(`${base}/music/${encodeURIComponent(file)}`,
          { headers: await authHeaders() });
        if (!res.ok) throw new Error(`Could not load preview (${res.status})`);
        return URL.createObjectURL(await res.blob());
      },

      // --- Co-pilot (Agent Session Substrate, Phase 4) ---

      // Run one co-pilot turn. `surface` is 'S2'..'S7'; `history` is the
      // prior [{role,text}] chat turns. Returns { output, validation, brief }.
      async copilotTurn(
        id: string, surface: string, message: string,
        history?: { role: string; text: string }[]
      ): Promise<any> {
        const r = await axios.post(`${base}/projects/${id}/copilot/turn`,
          { surface, message, history: history || [] },
          { headers: await authHeaders() });
        return r.data;
      },

      // End the project's co-pilot session.
      async endCopilot(id: string): Promise<any> {
        const r = await axios.delete(`${base}/projects/${id}/copilot`,
          { headers: await authHeaders() });
        return r.data;
      },

      // --- Storyboard (pre-render preview of the cut) ---

      // A still frame per edit-plan segment, returned as base64 data URLs
      // ready to drop straight into <img src>.
      async loadStoryboardThumbnails(
        id: string
      ): Promise<{ index: number; dataUrl: string | null }[]> {
        const r = await axios.get(`${base}/projects/${id}/storyboard/thumbnails`,
          { headers: await authHeaders() });
        return Array.isArray(r.data?.thumbnails) ? r.data.thumbnails : [];
      },

      // A one-line co-pilot description per shot. Slow — it runs a co-pilot
      // turn — so callers should treat it as a background fill.
      async describeStoryboard(
        id: string
      ): Promise<{ index: number; title?: string; description?: string }[]> {
        const r = await axios.post(`${base}/projects/${id}/storyboard/describe`, {},
          { headers: await authHeaders() });
        return Array.isArray(r.data?.shots) ? r.data.shots : [];
      },

      // Fetch the finished render as an object URL for inline <video>
      // playback. GET /download is auth-gated, so a plain <video src> cannot
      // stream it — the caller must revoke the URL when done.
      async loadRenderUrl(id: string): Promise<string> {
        const res = await fetch(`${base}/projects/${id}/download`, { headers: await authHeaders() });
        if (!res.ok) throw new Error(`Could not load the video (${res.status})`);
        return URL.createObjectURL(await res.blob());
      },

      // Fetch the finished render as a blob and trigger a browser download —
      // GET /download is auth-gated, so a plain <a href> will not work.
      async downloadRender(id: string, fileName: string): Promise<void> {
        const res = await fetch(`${base}/projects/${id}/download`, { headers: await authHeaders() });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'video.mp4';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      },
    };
  }, [user, instance]);
}

export type VideoEditorApi = ReturnType<typeof useVideoEditorApi>;
